import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, Field, Input, MessageBar, MessageBarBody, Spinner, Text, Title1, Title2, makeStyles, tokens } from "@fluentui/react-components";
import { ApprovalSummary, useApiClient } from "../hooks/useApiClient";

const useStyles = makeStyles({
  container: { display: "flex", flexDirection: "column", gap: "24px" },
  header: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "16px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: "16px" },
  card: { display: "flex", flexDirection: "column", gap: "12px" },
  muted: { color: tokens.colorNeutralForeground2 },
});
const isOpen = (status: string) => ["pending", "awaiting_second_approval"].includes(status.toLowerCase());
const statusLabels: Record<string, string> = {
  pending: "Awaiting level 1 approval",
  awaiting_second_approval: "Awaiting level 2 approval",
  approved: "Approved", rejected: "Rejected", expired: "Expired", timeout: "Expired",
};
const currency = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

export default function MyRequestsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { getMyRequests } = useApiClient();
  const [requests, setRequests] = useState<ApprovalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [search, setSearch] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let busy = false;
    const load = async () => {
      if (busy) return;
      busy = true;
      setRefreshing(true);
      try {
        const data = await getMyRequests(controller.signal);
        if (controller.signal.aborted) return;
        setRequests(data.requests);
        setUpdatedAt(new Date());
        setError(null);
      } catch {
        if (!controller.signal.aborted) setError("Could not refresh your requests. Please try again.");
      } finally {
        busy = false;
        if (!controller.signal.aborted) { setLoading(false); setRefreshing(false); }
      }
    };
    void load();
    const timer = window.setInterval(() => { if (!document.hidden) void load(); }, 60_000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [getMyRequests, refresh]);

  const query = search.trim().toLowerCase();
  const filtered = requests.filter(r => r.orderNumber.toLowerCase().includes(query) || r.supplier?.toLowerCase().includes(query));
  const open = filtered.filter(r => isOpen(r.status));
  const past = filtered.filter(r => !isOpen(r.status));
  const renderCards = (items: ApprovalSummary[]) => (
    <div className={styles.grid}>
      {items.map(request => (
        <Card key={request.id} className={styles.card}>
          <Text size={500} weight="semibold">Order #{request.orderNumber}</Text>
          <Badge appearance="tint" color={request.status === "approved" ? "success" : request.status === "rejected" ? "danger" : isOpen(request.status) ? "warning" : "informative"}>
            {statusLabels[request.status.toLowerCase()] ?? request.status}
          </Badge>
          <Text>Supplier: {request.supplier || "Not available"}</Text>
          <Text>Investment: {currency.format(request.totalInvestment)}</Text>
          <Text>CFROI: {(request.portfolioCfroi * 100).toFixed(2)}%</Text>
          <Text className={styles.muted}>Requested: {new Date(request.createdAt).toLocaleString()}</Text>
          {isOpen(request.status) && <Text className={styles.muted}>Expires: {new Date(request.expiresAt).toLocaleString()}</Text>}
          <Button onClick={() => navigate(`/details/${request.id}`, { state: { myRequestsReturn: true } })}>View details</Button>
        </Card>
      ))}
    </div>
  );
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title1>My requests</Title1>
        <Button appearance="primary" onClick={() => navigate("/approval")}>New request</Button>
        <Button disabled={refreshing} onClick={() => setRefresh(n => n + 1)}>{refreshing ? "Refreshing…" : "Refresh"}</Button>
      </div>
      <Text className={styles.muted}>Track your open approvals and review your past purchase requests. Status refreshes every minute while this page is visible.</Text>
      {error && <MessageBar intent="error"><MessageBarBody>{error}{updatedAt ? " Previously loaded results are shown." : ""}</MessageBarBody></MessageBar>}
      {loading ? <Spinner label="Loading your requests..." /> : <>
        <Field label="Find an order or supplier"><Input value={search} onChange={(_, data) => setSearch(data.value)} placeholder="Order number or supplier…" /></Field>
        {updatedAt && <Text size={200} className={styles.muted}>Last updated: {updatedAt.toLocaleTimeString()}</Text>}
        <Title2>Open requests ({open.length})</Title2>
        {open.length ? renderCards(open) : <Text>No open requests{query ? " match your search" : ""}.</Text>}
        <Title2>Past requests ({past.length})</Title2>
        {past.length ? renderCards(past) : <Text>No past requests{query ? " match your search" : ""}.</Text>}
      </>}
    </div>
  );
}
